import React, { useLayoutEffect, useRef, useState, useEffect } from 'react'
import { useFocusEffect } from '@react-navigation/native';
import {
  View, ViewPropTypes, StatusBar, BackHandler, FlatList, Platform,
  TouchableOpacity,
  Dimensions,
  Pressable,
  ScrollView
} from 'react-native'
import { useNavigation, useRoute } from '@react-navigation/native'

import {
  findIndex as _findIndex,
  isEqualWith as _isEqualWith,
  has as _has,
  isEqual as _isEqual,
  isUndefined as _isUndefined,
  get as _get,
  filter as _filter,
  isFunction as _isFunction,
} from 'lodash';
import PropTypes from 'prop-types';

import FoundationTestKeys from '../../FoundationTestKeys';
import { LightThemeColors } from '../Themes';
import { withTheme } from '../Themes/Provider';
import VFHeader from '../VFHeader';


import styles, { headerNonScrollMargin } from './VFScreen.Styles';

import VFText from '../VFText'
import { Icon } from '../Icon';
import VFButton from '../VFButton';

import { style } from '@vfgroup-oneplatform/framework/Themes'
import { useDispatch, useSelector } from 'react-redux'
import ReduxCache from './../../../../../App/Utils/ReduxCache'

import { getThemeImages, useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes'

import Modal from 'react-native-modal'

import launchLoginScreen from '../../../../../App/Utils/LaunchLogin'
import { getMsisdnData, isFixUser } from '../../../../../App/Utils'
import { VFHeaderTabs } from '@vfgroup-oneplatform/foundation/Components/VFHeaderTabs';
import { store } from '../../../../../App/Redux';
import NavigationService from '../../../../../App/Navigation/NavigationService';

const isEqualStackEntry = (stackEntry, currentValues) => {
  const equalityObject = {};

  const isEqualEntry = _isEqualWith(stackEntry, currentValues, (entry, current, key) => {
    if (key === 'barStyle' || key === 'backgroundColor' || key === 'translucent') {
      equalityObject[key] = _isEqual(entry, current);
    }

    if (
      _has(equalityObject, 'barStyle') &&
      _has(equalityObject, 'backgroundColor') &&
      _has(equalityObject, 'translucent')
    ) {
      return (
        equalityObject.barStyle && equalityObject.backgroundColor && equalityObject.translucent
      );
    }
  });

  return isEqualEntry;
};

// Async method because it is recommended by react native to mark the handling to be async to optimize performance and reduce the delay
const handleStatusBar = async (withStatusBar, clearStatusBarEntries) => {
  if (!withStatusBar || !clearStatusBarEntries) {
    return;
  }

  const { _currentValues: currentValues, _propsStack: propsStack } = StatusBar;
  const entriesByVFScreen = _filter(propsStack, stackEntry =>
    isEqualStackEntry(stackEntry, currentValues),
  );

  if (entriesByVFScreen.length === 1) {
    const entryIndex = _findIndex(propsStack, stackEntry =>
      isEqualStackEntry(stackEntry, currentValues),
    );

    if (entryIndex > 0) {
      const previousStatusBarEntry = propsStack[entryIndex - 1];
      const previousStatusBarEntryTranslucent = _get(previousStatusBarEntry, 'translucent', null);
      if (previousStatusBarEntryTranslucent) {
        setTimeout(() => {
          Platform.OS == 'android' && StatusBar.setTranslucent(previousStatusBarEntryTranslucent);
        }, 0);
      }
    }
  }
};

const onBackButtonPress = (withStatusBar, clearStatusBarEntries) => {
  handleStatusBar(withStatusBar, clearStatusBarEntries);
  return false;
};

const onClosePress = (onClose, withStatusBar, clearStatusBarEntries) => () => {
  if (withStatusBar && clearStatusBarEntries) {
    const { _currentValues: currentValues, _propsStack: propsStack } = StatusBar;

    const entryIndex = _findIndex(propsStack, stackEntry =>
      isEqualStackEntry(stackEntry, currentValues),
    );

    if (entryIndex > 0) {
      const previousStatusBarEntry = propsStack[entryIndex - 1];
      const previousStatusBarEntryTranslucent = _get(previousStatusBarEntry, 'translucent', null);

      if (previousStatusBarEntryTranslucent) {
        setTimeout(() => {
          Platform.OS == 'android' && StatusBar.setTranslucent(previousStatusBarEntryTranslucent);
        }, 0);
      }
    }
  }

  onClose();
};

const VFScreen = React.forwardRef(
  (
    {
      containerStyle,
      children,
      withStatusBar,
      keyboardShouldPersistTaps,
      clearStatusBarEntries,
      statusBarProps,
      titleTextHeaderAccessibilityLabel,
      onClose,
      theme,
      getScrollViewRef,
      handleOnScroll = (y = 0) => null,
      contentContainerStyle,
      withHeader,
      withHeaderSpace,
      isFullHeight,
      shouldSHowModal,
      setShouldShowModal,
      childrenStyle,
      closeButtonAccessibilityLabel,
      closeButtonTestID,
      customHeaderContent,
      ...headerProps
    },
    ref,
  ) => {
    const [headerHeight, setHeaderHeight] = useState(0);
    const backHandler = useRef(undefined);
    const route = useRoute();
    const navigation = useNavigation()
    const { navigate } = navigation
    const [isModalVisible, setIsModalVisible] = useState(false)
    const [userNameTags, setUsernameTags] = useState('')
    const language = useSelector(state => state.language)
    const userSideBar = useSelector(store => store.sidebar)

    useLayoutEffect(() => {
      if (withStatusBar && _isUndefined(backHandler.current)) {
        backHandler.current = BackHandler.addEventListener('hardwareBackPress', () =>
          onBackButtonPress(withStatusBar, clearStatusBarEntries),
        );
      } else if (!withStatusBar && !_isUndefined(backHandler.current)) {
        const removeHandler = _get(backHandler, 'current.remove', () => { });
        removeHandler();
      }
    }, [withStatusBar, backHandler, clearStatusBarEntries]);

    // handle header animation
    const _handleAnimationOnScroll = event => {
      if (ref && ref.current) {
        const { handleAnimationOnScroll } = ref.current;
        if (_isFunction(handleAnimationOnScroll)) {
          handleAnimationOnScroll(event);
        }
      }
    };
    const subHeaderMaxHeight = !headerProps.subHeaderMaxHeight
      ? 110 //subHeaderMaxHeight default value in VFHeader
      : headerProps.subHeaderMaxHeight;
    const scrollThrottle = headerProps.hideMainHeader
      ? 170
      : withHeaderSpace
        ? headerHeight
        : Platform.select({
          ios: 150 + subHeaderMaxHeight,
          android: 130 + subHeaderMaxHeight,
        });
    const renderChildren = () => {
      if (headerProps.configureHeaderTabs) {
        const { scrollEnabled = true, showsVerticalScrollIndicator } =
          headerProps.configureHeaderTabs;
        return (
          <FlatList
            showsVerticalScrollIndicator={showsVerticalScrollIndicator}
            scrollEnabled={scrollEnabled}
            keyboardShouldPersistTaps={keyboardShouldPersistTaps}
            scrollEventThrottle={1}
            onScroll={event => {
              _handleAnimationOnScroll(event);
              if (Platform.OS !== 'web' && Dimensions.get('window').width < 700) {
                _handleAnimationOnScroll(event)
                handleOnScroll(event.nativeEvent.contentOffset.y)
              }
            }}
            ref={getScrollViewRef}
            data={[
              <View
                style={[
                  styles.minHeight,
                  {
                    marginTop: scrollThrottle,
                  },
                ]}
              >
                {children || <View />}
              </View>,
            ]}
            contentContainerStyle={contentContainerStyle}
            renderItem={({ item }) => item}
            keyExtractor={(item, index) => index + ''}
            testID={FoundationTestKeys.VFScreenMainScroll}
          />
        );
      }
      const _headerNonScrollMargin = withHeaderSpace
        ? styles.headerNonScrollWithSpace(headerHeight)
        : headerNonScrollMargin;
      return <View style={[styles.minHeight, _headerNonScrollMargin, contentContainerStyle]}>{children}</View>;
    };
    const onHeaderHeightChange = (headerHeightValue, withHeaderTabs) => {
      // using Math.max as header height will change when scrolling down so i'm interested in max height for header
      // get max height only when header has tabs (because of scrolling ) otherwise set it with  actual height
      setHeaderHeight(
        withHeaderTabs ? Math.max(headerHeight, headerHeightValue) : headerHeightValue,
      );
    };

    const getVFHeaderProps = () => {
      return {
        onHeaderHeightChange: value => onHeaderHeightChange(value, headerProps.configureHeaderTabs),
        ref: ref,
        titleTextAccessibilityLabel: titleTextHeaderAccessibilityLabel,
        ...headerProps,
        onClose: onClosePress(onClose, withStatusBar, clearStatusBarEntries),
      };
    };

    const encryptedMsisdn = ReduxCache.get('auth.hashedMsisdnParking')
    const menus = [
      {
        key: 'Dashboard',
        name: 'dashboard_title',
        screens: ['Dashboard']
      },
      {
        key: 'ProductsAndServices',
        name: 'sub_tray_my_products_title',
        screens: ['ProductsAndServices']
      },
      {
        key: 'Billing',
        name: 'dashboard_billing_card_title',
        screens: ['Billing', 'MonthlyBilling', 'FixBilling']
      },
      {
        key: 'Catalogue',
        name: 'catalogue_title_screen',
        screens: ['Catalogue', 'CatalogueDetails']
      },
      {
        key: 'Shake',
        name: 'Shake',
        screens: ['Shake']
      },
      {
        key: 'Offers & Rewards',
        name: 'dashboard_offers_rewards_group_component_name',
        items: [
          {
            key: 'Demo',
            name: 'Vodafone More',
            screens: ['Demo', 'OpenedGift', 'Catalog', 'Profile', 'Info', 'Details', 'FavouritesDetails', 'VfCoinsTransfer', '']
          },
          {
            key: 'VfHearts',
            name: 'Flower box',
            screens: ['VfHearts', 'VfHeartsCollection']
          },
          {
            key: 'Quiz',
            name: 'UN Quiz',
            screens: ['Quiz', 'QuizGame']
          },
        ]
      },
      {
        key: 'Basic Services',
        name: 'basic_section_title',
        items: [
          {
            key: 'TopUPAnother',
            name: 'top_up_someone_else_quick_action_title',
            screens: ['TopUPAnother']
          },
          {
            key: 'TopUP',
            name: 'my_recharge_section_title',
            screens: ['TopUP']
          },
          {
            key: 'Parking',
            name: 'Tirana Parking',
            isWebView: true,
            params: { "url": `https://mytrp.altirana.com/?MSISDN=${encryptedMsisdn}&lang=${language}`, "title": "Tirana Parking", "isReact": true }
          },
        ]
      },
      {
        key: 'SOS Services',
        name: 'SOS Services',
        items: [
          {
            key: 'DataSharing',
            name: 'Vodafone Share',
            screens: ['DataSharing']
          },
          {
            key: 'CallMeBack',
            name: 'call_me_back_title',
            screens: ['CallMeBack', 'CallMeBackHistory']
          },
        ]
      },
      {
        key: 'Support',
        name: 'support_section_title',
        items: [
          {
            key: 'Roaming',
            name: 'Roaming',
            isWebView: true,
            params: { "url": "https://v1.vodafone.al/vodafone/Roaming_4510_1.php", "title": "Roaming", "isReact": true }
          },
        ]
      },
      {
        key: 'My Account',
        name: 'accounts_section_title',
        items: [
          // {
          //   key: 'My messages',
          //   name: 'messages_inbox_my_messages_title',
          //   screens: ['Demo']
          // },
          {
            key: 'PaymentMethods',
            name: 'payment_method_screen_title',
            screens: ['PaymentMethods', 'EditPayment', 'AddPayment']
          },
          {
            key: 'FixProductsAndServices',
            name: 'Giga Fibra',
            screens: ['FixProductsAndServices']
          },
          {
            key: 'Settings',
            name: 'subtray_app_settings_title',
            screens: ['Settings', 'ManageProfile', 'EditProfile', 'ChangeMyPassword', 'DeactivateAccount', 'Recommendations', 'Preferences', 'DataManagment', 'TermsAndConditions', 'PrivacyPolicy']
          },
        ]
      },
      {
        key: 'My Products',
        name: 'product_section_title',
        items: [
          // {
          //   key: 'ProductsAndServices',
          //   name: 'everything_is_ok_my_phone_title',
          //   screens: ['ProductsAndServices']
          // },
          {
            key: 'GigaFamily',
            name: 'Giga Family',
            screens: ['GigaFamily', 'GigaProductInfo', 'GigaBonus']
          },
          {
            key: 'FixContracts',
            name: 'my_contracts',
            screens: ['FixContracts']
          },
          {
            key: 'ManageOrders',
            name: 'my_orders',
            screens: ['ManageOrders']
          },
        ]
      },
    ]
    const [defaultMenu, setDefaultMenu] = useState(null)
    const [menu, setMenu] = useState(null)
    const dispatch = useDispatch()


    const logout = () => {
      setIsModalVisible(false)
      setTimeout(() => {
        // launchLoginScreen()
        ReduxCache.clear()
        window.localStorage.setItem('isLoggedInUAT', 'false')
        window.localStorage.setItem('isLoggedIn', 'false')
        NavigationService.navigateWithResetAction('PreLogin')
      }, 500)
    }

    const setUsernameFromStorage = () => {
      getMsisdnData()
        .then((res) => {
          const storedUsername = res?.username
          if (storedUsername) {
            const [first, last] = storedUsername.split(' ');
            setUsernameTags(first?.charAt(0) + (last?.charAt(0) || first?.charAt(1)))
          }
        })
        .catch(() => { })
    }

    useEffect(() => {
      setUsernameFromStorage()
      checkIfSettingsScreen()
    }, [])

    const [isHavingTabs, setIsHavingTabs] = useState(false)

    // const [activeTab, setActiveTab] = useState(0)

    const headerSettingTab = [
      {
        index: 0,
        key: 'GENERAL',
        tab_key: 'general_screen_title',
      },
      {
        index: 1,
        key: 'LANGUAGE',
        tab_key: 'language_screen_title',
      },
      {
        index: 2,
        key: 'PRIVACY',
        tab_key: 'privacy_screen_title',
      }
    ]


    const checkIfSettingsScreen = () => {
      if (Platform.OS === 'web' && Dimensions.get('window').width > 700) {
        if (['Settings', 'ManageProfile', 'EditProfile', 'ChangeMyPassword', 'DeactivateAccount', 'Recommendations', 'Preferences', 'DataManagment', 'TermsAndConditions', 'PrivacyPolicy'].filter((item) => item == route.name).length > 0) {
          setIsHavingTabs(true)
        } else {
          setIsHavingTabs(false)
        }
      }
    }

    const handleOnNavigation = () => {
      navigate('Settings')
    }

    const renderSideBar = () => {
      const theme = useTheme()
      const userType = ReduxCache.getUserType()
      if (userType !== 'guest') {
        return (
          <div
            style={styles.sidebar(isFullHeight, theme)}
          >
            {
              userSideBar.map((item) => {
                let isActive = item.screens?.includes(route.name)
                if (item.items) {
                  isActive = item.items.find(screen => {
                    if (screen.isWebView) {
                      return screen.params.url === route.params?.url
                    }
                    return screen.screens?.includes(route.name)
                  })
                  if (defaultMenu !== item.key && isActive)
                    setDefaultMenu(item.key)
                }
                return (
                  <>
                    <TouchableOpacity onPress={() => {
                      if (route.name === 'Catalogue') {
                        setShouldShowModal(false)
                      }
                      if (!item.items) {
                        setMenu(null)
                        navigate(item.key)
                      }
                      if (item.items)
                        setMenu(item.key)
                    }}
                      style={[styles.sidebarItem, isActive && styles.activeSidebarItem, (item.key === menu || item.key === defaultMenu) && styles.activeSidebarItem,]}
                    >
                      <VFText i18nKey={item.name} style={{
                        fontSize: 24,
                        color: theme.colors.textColor,
                        lineHeight: 32,
                        fontFamily: isActive ? "Vodafone Rg Bold" : "VodafoneLt"
                      }} />
                    </TouchableOpacity>
                    {
                      (item.key === menu || item.key === defaultMenu) && (
                        item.items.map(subItem => (
                          <TouchableOpacity onPress={() => {
                            setMenu(null)
                            if (subItem.isWebView) {
                              return navigate('WebView', subItem.params)
                            }
                            if (subItem.key === "Demo")
                              dispatch({
                                type: 'ACTIVE_COMPONENT',
                                payload: 'More'
                              })
                            if (subItem.key === "VfHearts") {
                              dispatch({
                                type: 'ACTIVE_COMPONENT',
                                payload: 'LoveBox'
                              })
                              if (menus[5].items[0].screens.includes(route.name)) {
                                navigate('Dashboard')
                              }
                            }
                            navigate(subItem.key)
                          }}
                            style={[styles.sidebarSubItem]}
                          >
                            <VFText i18nKey={subItem.name} style={{
                              fontSize: 20,
                              color: theme.colors.textColor,
                              lineHeight: 24,
                              fontFamily: (subItem.isWebView ? subItem.params.url === route.params?.url : subItem.screens?.includes(route.name)) ? "Vodafone Rg Bold" : "VodafoneLt"
                            }} />
                          </TouchableOpacity>
                        ))
                      )
                    }
                  </>
                )
              }
              )
            }
          </div>
        )
      }
    }




    return (
      <>
        {Platform.OS === 'web' && Dimensions.get('window').width > 700 ?
          <View style={{
            width: '100vw',
            height: 81,
            backgroundColor: theme.colors.backgroundColorSix,
            paddingLeft: 32,
            paddingTop: 16,
            justifyContent: 'space-between',
            shadowColor: '#171717',
            shadowOffset: { width: -2, height: 2 },
            shadowOpacity: 0.2,
            shadowRadius: 3,
            borderBottomWidth: 2,
            borderBottomColor: theme.colors.backgroundColorThree,
            flexDirection: 'row',
          }}>
            <Pressable onPress={() => { navigate('Dashboard') }}>
              <Icon
                type="image"
                size={48}
                name={getThemeImages().TImages.VFLogo.primary}
              />
            </Pressable>
            {ReduxCache.getUserType() !== 'guest' && <Pressable
              onPress={() => setIsModalVisible(true)}
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: '#007C92',
                alignItems: 'center',
                marginRight: 16,
                justifyContent: 'center'
              }}>
              <VFText i18nKey={userNameTags === '' ? '' : userNameTags} style={{ color: 'white', fontSize: 18 }} />
            </Pressable>}
          </View>
          : null}

        <View style={[styles.container(theme), containerStyle]}>

          {Platform.OS === 'web' && Dimensions.get('window').width > 700 ? renderSideBar()
            : null}

          <View style={[styles.children, childrenStyle]}>
            {withStatusBar && (
              <StatusBar
                translucent={false}
                backgroundColor={theme.colors.tintColor}
                barStyle={theme.name === 'light' ? 'dark-content' : 'light-content'}
              />
            )}
            {withHeader && (
              <VFHeader
                onHeaderHeightChange={value =>
                  onHeaderHeightChange(value, headerProps.configureHeaderTabs)
                }
                ref={ref}
                titleTextAccessibilityLabel={titleTextHeaderAccessibilityLabel}
                closeButtonAccessibilityLabel={closeButtonAccessibilityLabel}
                closeButtonTestID={closeButtonTestID}
                {...headerProps}
                onClose={onClosePress(
                  onClose,
                  withStatusBar,
                  clearStatusBarEntries
                )}
                customHeaderContent={customHeaderContent}
                subHeaderMaxHeight={subHeaderMaxHeight}

              />
            )}

            {isHavingTabs &&
              <VFHeaderTabs
                activeTab={headerProps?.activeTab}
                setActiveTab={headerProps?.setActiveTab}
                tabs={headerSettingTab}
                navigate={handleOnNavigation}
                style={{ backgroundColor: theme.colors.cardBackgroundColorFour, marginTop: 0 }}
              />}


            {!withHeader ? children : renderChildren()}
          </View>

        </View>

        <Modal
          isVisible={isModalVisible}
          backdropOpacity={0.5}
          onSwipeComplete={() => setIsModalVisible(false)}
          onBackdropPress={() => setIsModalVisible(false)}
          propagateSwipe
          hideModalContentWhileAnimating
          hardwareAccelerated
          backdropTransitionOutTiming={0}
          style={{
            justifyContent: 'flex-end',
            margin: '0 !important',
            minWidth: 1920,
            maxWidth: 1920,
          }}
        >
          <View
            style={{
              height: 'auto',
              minWidth: '600px',
              maxWidth: '600px',
              backgroundColor: theme.colors.quickActionGradientColors.secondary,
              margin: 'auto',
              padding: '50px',
              borderRadius: "7px",
            }}
            onStartShouldSetResponder={() => true}>
            <View style={styles.logoutModalContainer}>
              <VFText
                i18nKey='logout_description'
                style={styles.logoutConfirmTitle}
              />
              <VFButton
                title="logout_button"
                style={styles.logoutConfirmButton}
                onPress={logout}
              />
              <VFButton
                title="logout_cancel_button"
                type="secondary"
                style={styles.logoutConfirmButton}
                onPress={() => setIsModalVisible(false)}
              />
            </View>
          </View>
        </Modal>
      </>
    );
  },
);

VFScreen.defaultProps = {
  withStatusBar: true,
  clearStatusBarEntries: true,
  theme: LightThemeColors,
  withHeader: true,
  withHeader: true,
  isFullHeight: true
};

VFScreen.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.node,
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.node, PropTypes.element])),
  ]),
  keyboardShouldPersistTaps: PropTypes.oneOf(['always', 'never', 'handled', undefined]),
  containerStyle: ViewPropTypes.style,
  headerStyle: ViewPropTypes.style,
  titleStyle: PropTypes.object,
  contentContainerStyle: ViewPropTypes.style,
  showClose: PropTypes.bool,
  showBack: PropTypes.bool,
  leftIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  rightIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  hasShadow: PropTypes.bool,
  title: PropTypes.string,
  onBack: PropTypes.func,
  onClose: PropTypes.func,
  withStatusBar: PropTypes.bool,
  clearStatusBarEntries: PropTypes.bool,
  titleTextHeaderAccessibilityLabel: PropTypes.string,
  closeButtonAccessibilityLabel: PropTypes.string,
  closeButtonTestID: PropTypes.string,
  subHeaderTitleAccessibilityLabel: PropTypes.string,
  subHeaderSubTitleAccessibilityLabel: PropTypes.string,
  handleOnScroll: PropTypes.func,
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
  customHeaderContent: PropTypes.node,
  getScrollViewRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  withHeader: PropTypes.bool,
  withHeaderSpace: PropTypes.bool,
  statusBarProps: PropTypes.object,
};

export { handleStatusBar, onClosePress };

export default withTheme(VFScreen);
